import React, { useState } from 'react';

import { List, Modal, notification, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import * as api from 'config/axios';
import { CustomNotification, UploadFile, WithLoading } from 'common/components';

import { GET_EDITING_FIELDS_FORM_TEMPLATE } from 'services/product/endpoints';
import { UPLOAD_MAX_SIZE } from 'static/Constants';
import { useDispatchReloadPage } from 'hooks/useReloadPage';

import { useIntl } from 'react-intl';
import Messages from 'i18n/messages/product';
import {
  IXONECertificationWrapper,
  TYPE_VIEW,
} from 'common/components/product-add/IXONECertificationWrapper';
import useUpdateIXONEShield from 'common/components/product-add/useUpdateIXONEShield';
import { OVERLAY } from './constants';
import ValidateResultModal from '../../sections/product/components/upload-template/ValidateResultModal';
import { useMutationImportProduct } from '../../sections/product/components/upload-template/queries';

const { Text, Paragraph } = Typography;

const CONTINUE = 'continue';

const ImportProductBulkEditModal = (props) => {
  const intl = useIntl();
  const reloadPage = useDispatchReloadPage();

  const { updateIXONEShield, handleUpdateIXONEShield } = useUpdateIXONEShield();

  const {
    title,
    apiUrl,
    visible,
    closeModal,
    onSuccess,
    type,
    isGridView = false,
  } = props;

  const [errorList, setErrorList] = useState([]);
  const [fileUpload, setFileUpload] = useState();
  const [tdcProductIds, setTdcProductIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkEditLoading, setBulkEditLoading] = useState(false);
  const [validateResultModalState, setValidateResultModalState] = useState({
    visible: false,
    data: null,
    sessionId: null,
  });

  const { confirmImportBulkEditProduct } = useMutationImportProduct();

  const isOverlay = type === OVERLAY;

  const onCancel = () => {
    closeModal && closeModal();
    setErrorList([]);
  };

  const handleResponseUpload = async (response, ixoneIgnore) => {
    setErrorList([]);

    if (response?.isSuccess) {
      setBulkEditLoading(false);

      if (response?.data?.validationResults?.length > 0) {
        setValidateResultModalState({
          visible: true,
          data: response?.data?.validationResults,
          sessionId: response?.data?.sessionId,
          ixoneIgnore: ixoneIgnore,
        });

        return;
      }

      if (ixoneIgnore === CONTINUE && !isOverlay) {
        await handleUpdateIXONEShield(ixoneIgnore, tdcProductIds);
      }

      const systemMessage = response?.message;
      CustomNotification.success(
        systemMessage ||
          intl.formatMessage(Messages.importProductBulkEditSuccess)
      );
      isGridView &&
        reloadPage(); /* In fullview already validated in onSuccess(getLatestFullViewData) */

      onSuccess && onSuccess();

      closeModal && closeModal();
    } else {
      notification.error({
        message:
          response?.message ||
          intl.formatMessage(Messages.importProductBulkEditError),
      });
      if (response?.data?.validateFailDetails) {
        setErrorList(response?.data?.validateFailDetails);
      }
      setBulkEditLoading(false);
    }
  };

  const renderErrorList = () => {
    return (
      <div className='scroller' style={{ height: 200, paddingTop: '8px' }}>
        <List
          header={
            <Text style={{ fontWeight: 700 }} type='danger'>
              <InfoCircleOutlined /> Error
            </Text>
          }
          bordered
          dataSource={errorList}
          renderItem={(errorItem) => {
            const errorMessage = errorItem.reason || 'Invalid data';
            return (
              <List.Item>
                <Paragraph
                  type='danger'
                  style={{
                    marginBottom: 0,
                  }}
                >
                  <Text strong type='danger'>
                    {errorMessage}
                  </Text>{' '}
                  in{' '}
                  <Text strong type='danger'>
                    {errorItem?.sheetName}
                  </Text>{' '}
                  sheet, on row{' '}
                  <Text strong type='danger'>
                    {errorItem?.row}
                  </Text>
                  , column{' '}
                  <Text strong type='danger'>
                    {errorItem?.column}
                  </Text>
                </Paragraph>
              </List.Item>
            );
          }}
        />
      </div>
    );
  };

  const getFileChange = (paramsUpload) => {
    setFileUpload(paramsUpload.file.originFileObj);
  };

  const handleValidateFileUpload = (handleConfirmIXONE) => {
    setLoading(true);
    api
      .sendUpload({
        api: GET_EDITING_FIELDS_FORM_TEMPLATE,
        payload: {
          file: fileUpload,
        },
      })
      .then(({ data }) => {
        if (data?.isSuccess) {
          const productList = data?.data?.data;
          let Ids = [];
          productList?.forEach((product) => {
            const { ixOneId, ixOneCertifiedStatus, productId } = product;
            if (ixOneId && ixOneCertifiedStatus !== 'Removed') {
              Ids.push(+productId);
            }
          });
          setTdcProductIds(Ids);

          handleConfirmIXONE({
            originalData: Ids,
            changedFields: data?.data?.fields,
          });
          setLoading(false);
        } else {
          setLoading(false);
          CustomNotification.warning(data?.message ?? 'Server error');
        }
      })
      .catch(() => {
        setLoading(false);
        CustomNotification.warning('Server error');
      });
  };

  const handleUploadBulkEdit = (ixoneIgnore) => {
    const ixoneIgnoreOverlay = isOverlay ? ixoneIgnore : undefined;
    setBulkEditLoading(true);
    api
      .sendUpload({
        api: apiUrl,
        payload: {
          file: fileUpload,
        },
        additionalBodyPayload: isOverlay
          ? { ixoneIgnore: ixoneIgnoreOverlay }
          : null,
      })
      .then(async (response) => {
        await handleResponseUpload(response?.data, ixoneIgnore);
      })
      .catch(() => {
        CustomNotification.warning('Server error');
        setBulkEditLoading(false);
      });
  };

  const onSubmit = () => {
    try {
      const params = {
        sessionId: validateResultModalState?.sessionId,
      };

      confirmImportBulkEditProduct.mutate(params, {
        onSuccess: async (result) => {
          if (result?.isSuccess) {
            CustomNotification.success(
              result?.message || 'Edit Product(s) Successfully'
            );

            if (
              validateResultModalState?.ixoneIgnore === CONTINUE &&
              !isOverlay
            ) {
              await handleUpdateIXONEShield(
                validateResultModalState?.ixoneIgnore,
                tdcProductIds
              );
            }

            setValidateResultModalState({ visible: false, data: null });
            reloadPage();
            setBulkEditLoading(false);
            onSuccess && onSuccess();
            closeModal && closeModal();
          } else {
            CustomNotification.error(result?.message || 'Server Error');
          }
        },
      });
    } catch (error) {
      CustomNotification.error('Server Error');
    }
  };

  return (
    <IXONECertificationWrapper
      showModal
      type={TYPE_VIEW.BULK_EDIT}
      onConfirm={(ixoneIgnore) => {
        handleUploadBulkEdit(ixoneIgnore);
      }}
      onCancel={onCancel}
    >
      {(handleConfirmIXONE) => (
        <Modal
          visible={visible}
          title={title}
          onCancel={onCancel}
          footer={null}
          width={1000}
          destroyOnClose
        >
          <WithLoading
            loading={loading || bulkEditLoading || updateIXONEShield.isLoading}
            tip='Data is being validated'
          >
            <div style={{ height: 450, overflow: 'hidden' }}>
              <UploadFile
                uploadAPL //* for getting error list
                manualUpload={true}
                isBase64={false}
                showMultiple={false}
                supportTypes={['xlsx', 'xls']}
                showXlsTemplate={false}
                apiUrl={apiUrl}
                showUploadSuccessMsg={false}
                showServerMessage={false}
                notShowMessage={true}
                getFileChange={getFileChange}
                manualUploadHandler={() => {
                  handleValidateFileUpload(handleConfirmIXONE);
                }}
                maxSize={UPLOAD_MAX_SIZE.GENERAL}
              />
            </div>
          </WithLoading>
          {errorList?.length ? renderErrorList() : null}
          <div>
            {validateResultModalState?.visible && (
              <ValidateResultModal
                isBulkEdit
                onSubmit={onSubmit}
                validateResultModalState={validateResultModalState}
                setValidateResultModalState={setValidateResultModalState}
                loading={
                  confirmImportBulkEditProduct.isLoading ||
                  updateIXONEShield.isLoading
                }
              />
            )}
          </div>
        </Modal>
      )}
    </IXONECertificationWrapper>
  );
};

export default ImportProductBulkEditModal;
